import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Sun, Moon, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "../Components/Footer";
import Logo from "../Assets/Homeeframes/Homeeframes Liner (1).png";

const stats = [
  { value: "10+", label: "Years of Excellence" },
  { value: "50+", label: "Projects Delivered" },
  { value: "200+", label: "Happy Families" },
  { value: "3", label: "Cities Presence" },
];

const values = [
  {
    number: "01",
    title: "Design Excellence",
    description:
      "Every space we create is a balance of form and function — crafted with meticulous attention to detail and a passion for architectural beauty.",
  },
  {
    number: "02",
    title: "Trusted Craftsmanship",
    description:
      "We partner only with the finest builders and artisans, ensuring every project meets the highest standards of quality and durability.",
  },
  {
    number: "03",
    title: "Client First",
    description:
      "Your vision is our blueprint. We listen, collaborate, and deliver spaces that exceed expectations at every stage of the journey.",
  },
  {
    number: "04",
    title: "Sustainable Living",
    description:
      "From material selection to energy efficiency, sustainability is embedded in the DNA of every Homeeframes project.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" },
  }),
};

const AboutPage = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const bg = isDarkMode ? "bg-[#111111] text-white" : "bg-white text-black";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const accent = isDarkMode ? "text-[#FEF200]" : "text-black";
  const accentBg = isDarkMode ? "bg-[#FEF200] text-black" : "bg-black text-white";
  const cardBg = isDarkMode ? "bg-[#1a1a1a]" : "bg-[#F5F5F5]";
  const divider = isDarkMode ? "bg-gray-800" : "bg-gray-200";

  return (
    <div className={`min-h-screen w-full transition-colors duration-500 ${bg}`}>


      {/* ── HERO ── */}
      <section className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6 pt-28 pb-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="max-w-4xl mx-auto flex flex-col items-center gap-6"
        >
          <span className={`text-xs tracking-[0.3em] uppercase font-semibold px-4 py-2 rounded-full ${isDarkMode ? "bg-gray-800 text-[#FEF200]" : "bg-gray-100 text-gray-500"}`}>
            Who We Are
          </span>
          <h1 className="text-5xl md:text-7xl font-bold leading-tight tracking-tight">
            Building spaces that{" "}
            <span className={isDarkMode ? "text-[#FEF200]" : "text-gray-400"}>
              inspire lives.
            </span>
          </h1>
          <p className={`text-lg md:text-xl max-w-2xl leading-relaxed ${muted}`}>
            Homeeframes is a luxury real estate and architectural design firm based in Kerala,
            dedicated to creating homes that reflect the unique stories of the people who live in them.
          </p>
        </motion.div>
      </section>

      {/* ── STATS ── */}
      <section className={`py-16 px-6 md:px-16 lg:px-28 ${isDarkMode ? "bg-[#0d0d0d]" : "bg-[#F5F5F5]"}`}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto"
        >
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              custom={i}
              variants={fadeUp}
              className="flex flex-col items-center text-center gap-2"
            >
              <span className={`text-5xl font-bold tracking-tight ${accent}`}>
                {stat.value}
              </span>
              <span className={`text-sm font-medium ${muted}`}>{stat.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── STORY ── */}
      <section className="py-24 px-6 md:px-16 lg:px-28 max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center"
        >
          <div className="flex flex-col gap-6">
            <p className={`text-xs tracking-[0.3em] uppercase font-semibold ${accent}`}>
              Our Story
            </p>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight">
              A decade of crafting dream homes
            </h2>
            <p className={`text-base leading-relaxed ${muted}`}>
              Founded with a vision to redefine luxury living in Kerala, Homeeframes has spent
              over a decade transforming architectural concepts into breathtaking realities.
              We believe a home is more than walls — it is where memories are made.
            </p>
            <p className={`text-base leading-relaxed ${muted}`}>
              From concept to completion, our team of architects, designers, and project managers
              work in seamless harmony to deliver projects that stand as testaments to timeless
              craftsmanship and innovative design.
            </p>
            <button
              onClick={() => navigate("/contact")}
              className={`self-start px-8 py-3 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${accentBg}`}
            >
              Get in Touch
            </button>
          </div>

          {/* Decorative block */}
          <div className="relative h-80 md:h-96">
            <div className={`absolute inset-0 rounded-3xl ${isDarkMode ? "bg-[#1a1a1a]" : "bg-[#E8E8E8]"}`} />
            <div className={`absolute -bottom-6 -right-6 w-48 h-48 rounded-3xl ${isDarkMode ? "bg-[#FEF200]/10 border border-[#FEF200]/20" : "bg-black/5 border border-black/10"}`} />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-8 text-center">
              <span className={`text-7xl font-black ${isDarkMode ? "text-[#FEF200]" : "text-black"}`}>HF</span>
              <span className={`text-sm tracking-widest uppercase font-medium ${muted}`}>Homeeframes</span>
              <span className={`text-xs ${muted}`}>Est. 2014 · Thiruvananthapuram, Kerala</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── VALUES ── */}
      <section className={`py-24 px-6 md:px-16 lg:px-28 ${isDarkMode ? "bg-[#0d0d0d]" : "bg-[#F5F5F5]"}`}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <p className={`text-xs tracking-[0.3em] uppercase font-semibold mb-3 ${accent}`}>
            What Drives Us
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Our Core Values</h2>
          <div className={`mx-auto mt-4 h-[2px] w-14 rounded-full ${isDarkMode ? "bg-[#FEF200]" : "bg-black"}`} />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {values.map((val, i) => (
            <motion.div
              key={val.number}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className={`p-8 rounded-3xl flex flex-col gap-4 ${cardBg}`}
            >
              <span className={`text-4xl font-black opacity-20 ${accent}`}>{val.number}</span>
              <h3 className="text-xl font-bold">{val.title}</h3>
              <p className={`text-sm leading-relaxed ${muted}`}>{val.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer isDarkMode={isDarkMode} />
    </div>
  );
};

export default AboutPage;
